import {
  FormTemplate,
  FormSubmission,
  CreateFormTemplateDto,
  UpdateFormTemplateDto,
  CreateFormSubmissionDto,
  UpdateFormSubmissionDto,
  SearchFormTemplateDto,
  SearchFormSubmissionDto,
  FormTemplateResponse,
  FormSubmissionResponse,
  FormStatistics,
  FormSubmissionStatus,
  RespondToSubmissionDto,
} from '@/core/models/form.model'
import { SearchRequestOptions } from '@/core/types/request'
import { WithId } from '@/core/types/shared'
import { cacheByIdArg } from '@/utils/rtkQueryCacheUtils'
import { baseApi, Tag } from './base'

export const formApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Form Template Endpoints
    getFormTemplate: builder.query<FormTemplate, string>({
      query: (id) => `/forms/templates/${id}`,
      providesTags: cacheByIdArg(Tag.FORM_TEMPLATE),
    }),

    getActiveFormTemplates: builder.query<FormTemplateResponse, SearchRequestOptions>({
      query: (options) => ({
        url: '/forms/templates',
        params: {
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_TEMPLATE],
    }),

    searchFormTemplates: builder.query<FormTemplateResponse, { search: SearchFormTemplateDto } & SearchRequestOptions>({
      query: ({ search, ...options }) => ({
        url: '/forms/templates/search',
        params: {
          ...search,
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_TEMPLATE],
    }),

    createFormTemplate: builder.mutation<FormTemplate, CreateFormTemplateDto>({
      query: (body) => ({
        url: '/forms/templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: [Tag.FORM_TEMPLATE],
    }),

    updateFormTemplate: builder.mutation<FormTemplate, WithId<{ changes: UpdateFormTemplateDto }>>({
      query: ({ id, changes }) => ({
        url: `/forms/templates/${id}`,
        method: 'PUT',
        body: changes,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: Tag.FORM_TEMPLATE }, { type: Tag.FORM_TEMPLATE, id }],
    }),

    archiveFormTemplate: builder.mutation<FormTemplate, string>({
      query: (id) => ({
        url: `/forms/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [Tag.FORM_TEMPLATE],
    }),

    // Form Submission Endpoints
    getFormSubmission: builder.query<FormSubmission, string>({
      query: (id) => `/forms/submissions/${id}`,
      providesTags: cacheByIdArg(Tag.FORM_SUBMISSION),
    }),

    searchFormSubmissions: builder.query<FormSubmissionResponse, { search: SearchFormSubmissionDto } & SearchRequestOptions>({
      query: ({ search, ...options }) => ({
        url: '/forms/submissions/search',
        params: {
          ...search,
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_SUBMISSION],
    }),

    createFormSubmission: builder.mutation<FormSubmission, CreateFormSubmissionDto>({
      query: (body) => ({
        url: '/forms/submissions',
        method: 'POST',
        body,
      }),
      invalidatesTags: [Tag.FORM_SUBMISSION],
    }),

    updateFormSubmission: builder.mutation<FormSubmission, WithId<{ changes: UpdateFormSubmissionDto }>>({
      query: ({ id, changes }) => ({
        url: `/forms/submissions/${id}`,
        method: 'PUT',
        body: changes,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: Tag.FORM_SUBMISSION }, { type: Tag.FORM_SUBMISSION, id }],
    }),

    deleteFormSubmission: builder.mutation<void, string>({
      query: (id) => ({
        url: `/forms/submissions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [Tag.FORM_SUBMISSION],
    }),

    getPatientFormSubmissions: builder.query<FormSubmissionResponse, { patientId: string } & SearchRequestOptions>({
      query: ({ patientId, ...options }) => ({
        url: `/forms/submissions/patient/${patientId}`,
        params: {
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_SUBMISSION],
    }),

    getConsultationFormSubmissions: builder.query<FormSubmissionResponse, { consultationId: string } & SearchRequestOptions>({
      query: ({ consultationId, ...options }) => ({
        url: `/forms/submissions/consultation/${consultationId}`,
        params: {
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_SUBMISSION],
    }),

    getExpertiseFormSubmissions: builder.query<FormSubmissionResponse, { expertiseId: string } & SearchRequestOptions>({
      query: ({ expertiseId, ...options }) => ({
        url: `/forms/submissions/expertise/${expertiseId}`,
        params: {
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_SUBMISSION],
    }),

    // Statistics Endpoints
    getFormStats: builder.query<FormStatistics, string>({
      query: (templateId) => `/forms/${templateId}/stats`,
      providesTags: (_result, _error, templateId) => [{ type: Tag.FORM_TEMPLATE, id: templateId }],
    }),

    // CSV Export Endpoint
    exportFormSubmissionsToCSV: builder.query<{ csvContent: string; fileName: string }, string>({
      query: (templateId) => ({
        url: `/forms/${templateId}/export/csv`,
        method: 'GET',
        params: {
          // Add cache-busting parameter to force fresh request each time
          _t: Date.now(),
        },
      }),
      providesTags: (_result, _error, templateId) => [{ type: Tag.FORM_TEMPLATE, id: templateId }],
    }),

    makePdfFillable: builder.mutation<{ success: boolean; message: string; newFileKey?: string }, { fileKey: string }>({
      query: (body) => ({
        url: '/forms/make-fillable',
        method: 'POST',
        body,
      }),
    }),

    detectFields: builder.mutation<any[], string>({
      query: (templateId) => ({
        url: `/forms/templates/${templateId}/detect-fields`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, templateId) => [{ type: Tag.FORM_TEMPLATE, id: templateId }],
    }),

    burnPdf: builder.mutation<any, string>({
      query: (templateId) => ({
        url: `/forms/templates/${templateId}/burn-pdf`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, templateId) => [{ type: Tag.FORM_TEMPLATE, id: templateId }],
    }),

    generateFilledPdf: builder.mutation<string, string>({
      query: (submissionId) => ({
        url: `/forms/submissions/${submissionId}/generate-pdf`,
        method: 'POST',
        body: {},
      }),
    }),

    // Ethics Endpoints
    getEthicsSubmissions: builder.query<FormSubmissionResponse, { status?: FormSubmissionStatus } & SearchRequestOptions>({
      query: ({ status, ...options }) => ({
        url: '/forms/submissions/ethics',
        params: {
          ...(status && { status }),
          limit: Number(options.limit),
          skip: Number(options.skip),
          order: options.order,
          orderBy: options.orderBy || 'createdAt',
        },
      }),
      providesTags: [Tag.FORM_SUBMISSION],
    }),

    respondToSubmission: builder.mutation<FormSubmission, { id: string; body: RespondToSubmissionDto }>({
      query: ({ id, body }) => ({
        url: `/forms/submissions/${id}/respond`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [Tag.FORM_SUBMISSION],
    }),
  }),
  overrideExisting: false,
})
